import * as React from "react";
import { Link as RouterLink } from "react-router-dom";
import { Card, CardContent, Typography, CardActionArea, makeStyles, CardMedia, Chip } from "@material-ui/core";

import { BeaconWrapUpStory, ProjectParticipant } from "../../../api/graphQL/queryModels";
import { PersonList } from "../people";
import { ParticipantSorter } from "../../../utils";

const useStyles = makeStyles((theme) => ({
    root: {
        maxWidth: 345
    },
    unReviewedMarker: {
        position: "absolute",
        top: 0,
        right: 0,
        padding: theme.spacing(1),
        background: theme.palette.warning.main,
        borderBottomLeftRadius: 8,
        color: theme.palette.getContrastText(theme.palette.warning.main)
    },
    reviewedMarker: {
        position: "absolute",
        top: 0,
        right: 0,
        padding: theme.spacing(1),
        background: theme.palette.primary.main,
        borderBottomLeftRadius: 8,
        color: theme.palette.getContrastText(theme.palette.primary.main)
    },
    archiveMarker: {
        position: "absolute",
        top: 0,
        right: 0,
        padding: theme.spacing(1),
        background: theme.palette.error.main,
        borderBottomLeftRadius: 8,
        color: theme.palette.common.white
    },
    approvedMarker: {
        position: "absolute",
        top: 0,
        right: 0,
        padding: theme.spacing(1),
        background: theme.palette.success.main,
        borderBottomLeftRadius: 8,
        color: theme.palette.common.white
    },
    wrapUpBadge: {
        position: "absolute",
        top: theme.spacing(1),
        left: theme.spacing(1),
        backgroundColor: "#00675B",
        color: theme.palette.common.white,
        fontWeight: 700,
        zIndex: 1
    },
    media: {
        height: "9.25rem"
    },
    content: {
        height: "9.25rem"
    },
    clamp: {
        overflow: "hidden",
        textOverflow: "ellipsis",
        display: "-webkit-box",
        "-webkit-line-clamp": 1,
        "-webkit-box-orient": "vertical"
    },
    storyCard: {
        height: "18.5rem",
        width: "25.75rem",
        margin: theme.spacing(1),
        position: "relative"
    }
}));

interface WrapUpStoryListItemProps {
    story: BeaconWrapUpStory;
}

const WrapUpStoryListItem = (props: WrapUpStoryListItemProps) => {
    const classes = useStyles();
    const { story } = props;
    const participants: ProjectParticipant[] = ParticipantSorter.filterAndSortProjectParticipants(story.project?.participants || []);

    return (
        <Card className={classes.storyCard}>
            <Chip label="WRAP-UP" className={classes.wrapUpBadge} size="small" />
            <CardActionArea component={RouterLink} to={`/wrap-up-stories/${story.id}`}>
                {!story.isPublished && <div className={classes.unReviewedMarker}>UNREVIEWED</div>}
                {story.isPublished && !story.isArchived && !story.isApproved && <div className={classes.reviewedMarker}>REVIEWED</div>}
                {story.isApproved && !story.isArchived && <div className={classes.approvedMarker}>APPROVED</div>}
                {story.isArchived && <div className={classes.archiveMarker}>Archived</div>}
                <CardMedia
                    className={classes.media}
                    component="img"
                    image={story.headerImageUrl ?? "/img/no_header.jpg"}
                    title=""
                    onError={(e: React.ChangeEvent<HTMLImageElement>) => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.src = "/img/no_header.jpg";
                    }}
                />
                <CardContent className={classes.content}>
                    <Typography variant="h6" className={classes.clamp}>
                        {story.title}
                    </Typography>
                    <Typography gutterBottom variant="caption" color="textSecondary" className={classes.clamp}>
                        {story.project?.studios.map((s) => s.name).join(" • ") || "\u00a0"}
                    </Typography>
                    <Typography gutterBottom variant="body1" color="textPrimary" className={classes.clamp}>
                        {story.previewDescription}
                    </Typography>
                    {!!participants.length && (
                        <PersonList
                            people={participants.map((p) => p.person)}
                            size="md"
                            showInactivePeople
                            showText={false}
                            truncate={true}
                        />
                    )}
                </CardContent>
            </CardActionArea>
        </Card>
    );
};

export default WrapUpStoryListItem;
